<template>
  <div
    class="search-autocomplete-wrapper"
    :class="{open: isAutocompleteVisible}"
  >
    <form
      id="search_mini_form"
      class="form minisearch"
      @submit.prevent="debounceInput"
      @keyup.escape.prevent="hideAutocomplete()"
    >
      <div
        class="field search"
        @keydown.up="updateIndex($event)"
        @keydown.down="updateIndex($event)"
      >
        <label
          class="label sr-only"
          for="search"
          data-role="minisearch-label"
        >
          <span>Search</span>
        </label>

        <div class="control-background-wrapper">
          <div class="control">
            <input
              id="search"
              ref="search"
              v-model="searchTerm"
              :aria-activedescendant="activedescendant"
              :aria-expanded="isAutocompleteVisible"
              aria-autocomplete="list"
              aria-controls="trending-searches"
              autocomplete="off"
              class="input-text"
              data-cnstrc-search-input
              enterkeyhint="search"
              maxlength="128"
              name="q"
              placeholder="Search products, colors, or brands"
              role="combobox"
              type="text"
              @click="greet"
              @keyup="debounceInput"
            >
            <span
              role="presentation"
              class="icon-search"
            ></span>
            <button
              type="button"
              aria-label="close search"
              class="icon-close"
              @click="clear"
            ></button>
          </div>
        </div>

        <Autocomplete
          v-show="isAutocompleteVisible"
          ref="autocomplete"
          :products="products"
          :response-top-products="responseTopProducts"
          :response-top-suggestions="responseTopSuggestions"
          :search-term="searchTerm.trim()"
          :selected-element="activedescendant"
          :suggestions="suggestions"
          :suggestions-class="suggestionsClass"
          @recent-click="updateSearchTerm"
          @suggestion-click="updateSearchTerm"
          @product-click="setRecentlySearched"
        />
      </div>
    </form>

    <Teleport to=".page-wrapper">
      <div id="autocomplete-overlay"></div>
    </Teleport>
  </div>
</template>

<script>

import Autocomplete from "./Autocomplete.vue";

import ConstructorIOClient from '@constructor-io/constructorio-client-javascript';
import debounce from 'lodash/debounce';
import { trackSearchTerm, trackSiteSearchClicked, getCurrentPageType } from '@/composables/segment.js';

export default {
  name: "SearchBox",
  components: {
    Autocomplete: Autocomplete
  },
  data() {
    return {
      activedescendant: null,
      index: null,
      isAutocompleteVisible: false,
      previousSearchTerm: '',
      previousProductStore: '',
      previousSuggestionsStore: '',
      products: [],
      responseTopProducts: null,
      responseTopSuggestions: null,
      searchTerm: '',
      speechService: {},
      suggestions: [],
      suggestionsClass: '',
      topProductsStore: [],
      topSuggestionsStore: []
    }
  },
  watch: {
    searchTerm() {
      this.index = null;
    },
    index() {
      this.setActiveDescendant();
    },
  },
  created() {
    this.searchTerm = this.getSearchTermFromURL();

    this.debounceInput = debounce(this.input, 300);

    document.onclick = this.clickOutside;

    window['cnstrc-data'] = window['cnstrc-data'] || {};
  },
  methods: {
    clickOutside(event) {
      // If the modal isn't open, we don't need to worry about closing it. exit.
      if (this.isAutocompleteVisible === false) {
        return;
      }

      // If we're opening autocomplete from mobile PDP search icon
      if (event.target === document.querySelector('button.icon-open')) {
        return;
      }

      // .isTrigger is a jQuery property that does not appear on browser
      // generated events. If it is appearing in the event, we can safely
      // assume we're outside of the autocomplete because it uses no jQuery.
      // This check is here because jQuery generated events don't have
      // .closest on event.target and throw an error.
      // If #search_mini_form is a parent or child node of the event target
      // then we can assume we're clicking somewhere inside of the scope of
      // autocomplete and therefore don't need to do anything
      if (event.isTrigger === undefined &&
          event.target.closest('#search_mini_form') !== null) {
        return;
      }

      this.clear();
    },
    clear() {
      this.products = [];
      this.suggestions = [];
      this.suggestionsClass = '';
      this.searchTerm = '';
      this.hideAutocomplete();
    },
    getSearchClient() {
      let searchClientConfig = {
        apiKey: window.search.apiKey,
        fetch: fetch
      };

      if (window.cnstrcUserId) {
        searchClientConfig.userId = window.cnstrcUserId;
      } else {
        let mageCacheStorage = JSON.parse(localStorage.getItem('mage-cache-storage'));

        if (mageCacheStorage?.customer?.id) {
          searchClientConfig.userId = mageCacheStorage.customer.id;
        }
      }

      return new ConstructorIOClient(searchClientConfig);
    },
    input(event) {
      if (event.key === 'Enter' && this.activedescendant) {
        let elm = document.getElementById(this.activedescendant);

        if (elm) {
          this.searchTerm = elm.innerText;
        }
      }

      let term = this.searchTerm.trim();

      if (event.key === 'Enter' && term.length > 0) {
        Promise.allSettled([this.search()])
        .then(() => {
          this.setRecentlySearched();
          let loc;

          let skuSearch = this.products.find((product) => {
            return product.data.id.toLowerCase() === term.toLowerCase();
          });

          if (skuSearch) {
            loc = this.products[0].data.url;
          } else {
            let url = new URL(window.location.origin + '/catalogsearch/result/');
            url.searchParams.append('q', term);
            loc = url.toString();
          }

          if (loc) {
            location.assign(loc);
          }
        });

        return;
      }

      // We don't want to trigger search which will open the autocomplete when
      // we're trying to close the autocomplete with the escape key.
      if(event.key !== 'Escape' && event.key !== 'Esc') {
        this.search();
      }
    },
    search() {
      let autocomplete = this.getSearchClient().autocomplete,
          sectionKey = this.isMobile() ? 'mobile' : 'desktop',
          self = this;

      // Prevent empty searches
      if (this.searchTerm.trim() === '') {
        this.setTopSuggestionsAndProducts();
        return;
      }

      // Prevent Redunant AJAX calls
      if (this.searchTerm === this.previousSearchTerm &&
      (this.previousProductStore.length !== 0 && this.previousSuggestionsStore.length !== 0)) {
        this.products = this.previousProductStore;
        this.suggestions = this.previousSuggestionsStore;
        this.showAutocomplete();
        return;
      }

      let options = {
        resultsPerSection: {
          Products: window.search.config[sectionKey].visual,
          'Search Suggestions': window.search.config[sectionKey].search,
        },
        variationsMap: this.getVariationsMap(),
      };

      return autocomplete.getAutocompleteResults(
          this.searchTerm,
          options
      ).then(function (json) {
        if (json.sections.Products.length === 0) {
          // Check if topProductsStore has products.  This will be populated if
          // greet has fired but not if we start on the search results page
          // and the input has been pre-populated by the q param.
          if (self.topProductsStore.length > 0) {
            self.products = self.topProductsStore;
          } else {
            self.getTopProducts();
          }

          self.suggestions = [];
        } else {
          self.products = json.sections.Products || [];
          self.suggestions = json.sections['Search Suggestions'] || [];

          // Cache previous results to be used if the user clicks out of
          // autocomplete and back in.
          self.previousSearchTerm = self.searchTerm;
          self.previousProductStore = self.products;
          self.previousSuggestionsStore = self.suggestions;
        }

        self.suggestionsClass = 'autocomplete-suggestions';

        self.showAutocomplete();

        trackSearchTerm(
          json?.request?.term,
          json?.total_num_results_per_section?.['Products']
        );
      });
    },
    greet(event) {
      event.target.select();

      if (this.isAutocompleteVisible) {
        return;
      }

      if (this.searchTerm === '') {
        this.setTopSuggestionsAndProducts();
      } else {
        this.search();
      }

      trackSiteSearchClicked(this.$refs.search, getCurrentPageType());
    },
    getTopSuggestions() {
      let recommendations = this.getSearchClient().recommendations,
          sectionKey = this.isMobile() ? 'mobile' : 'desktop',
          self = this;

      // Get Top Search Suggestions Pod
      return recommendations.getRecommendations(
          'top_search_terms',
          {
            numResults: window.search.config[sectionKey].search,
            section: 'Search Suggestions'
          }
      ).then(function (json) {
        self.responseTopSuggestions = json;
        self.suggestions = json.response.results || [];
        self.topSuggestionsStore = json.response.results || [];
        self.suggestionsClass = 'recommendation-suggestions';
        self.suggestionsSearchPod = json.response.pod?.id || '';
        self.suggestionsSearchInitialPod = self.suggestionsSearchInitialPod || json.response.pod?.id || '';
      });
    },
    getTopProducts() {
      let recommendations = this.getSearchClient().recommendations,
          sectionKey = this.isMobile() ? 'mobile' : 'desktop',
          self = this;

      let options = {
        numResults: window.search.config[sectionKey].visual,
        section: 'Products',
        variationsMap: this.getVariationsMap(),
      }

      // Get Top Products Pod
      return recommendations.getRecommendations(
          'top_products',
          options
      ).then(function (json) {
        self.responseTopProducts = json;
        self.products = json.response.results || [];
        self.topProductsStore = json.response.results || [];
      });
    },
    setTopSuggestionsAndProducts() {
      let callsToMake = [];

      if (this.topProductsStore.length === 0) {
        callsToMake.push[this.getTopProducts()];
      } else {
        this.products = this.topProductsStore;
      }

      if (this.topSuggestionsStore.length === 0) {
        callsToMake.push[this.getTopSuggestions()];
      } else {
        this.suggestions = this.topSuggestionsStore;
      }

      Promise.all(callsToMake).then(() => {
        this.showAutocomplete();
      });
    },
    updateIndex(event) {
      /**
       * Fix for M2PL-6522. Prevent combobox from preventing Up/Down arrow keys
       * from breaking radiogroup navigation.
       */
      if (event.target.classList.contains('swatch-radio')) {
        return;
      }

      if (this.suggestions.length === 0) {
        return;
      }

      let lastIndex = this.suggestions.length - 1; // index are 0-based
      let newIndex = this.index ?? -1;

      let hasRecentlySearched = this.getRecentlySearched()?.length === 3;
      let isSearching = this.searchTerm?.length > 0;
      if (hasRecentlySearched && !isSearching) {
        let config = window?.search?.config || {};
        let sectionKey = this.isMobile() ? 'mobile' : 'desktop';
        let optionKey = hasRecentlySearched ? 'withRecent' : 'search';

        lastIndex = config[sectionKey][optionKey] - 1;
      }

      if (event.key === 'ArrowUp') {
        newIndex--;
      } else if (event.key === 'ArrowDown') {
        newIndex++;
      }

      if (newIndex < 0) {
        newIndex = lastIndex;
      }

      if (newIndex > lastIndex) {
        newIndex = 0;
      }

      this.index = newIndex;
    },
    updateSearchTerm(suggestion) {
      if (suggestion) {
        this.setRecentlySearched(suggestion);
      } else {
        this.setRecentlySearched();
      }
    },
    hideAutocomplete() {
      document.querySelector('body').classList.remove('autocomplete-open');
      document.querySelector('#autocomplete-overlay').classList.remove('show');
      this.isAutocompleteVisible = false;
      this.index = null;
    },
    showAutocomplete() {
      document.querySelector('body').classList.add('autocomplete-open');
      document.querySelector('#autocomplete-overlay').classList.add('show');
      this.isAutocompleteVisible = true;
    },
    setActiveDescendant() {
      let autocompleteContainer = this.$refs.autocomplete.$el.querySelector('[role="listbox"]');

      if (this.index === null) {
        this.activedescendant = null;
      } else {
        this.activedescendant = autocompleteContainer.children[this.index].id;
      }
    },
    isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    },
    onSearchPage() {
      // Check if on catalog search page
      let searchPath = '/catalogsearch/result/';
      return window.location.pathname.startsWith(searchPath);
    },
    setRecentlySearched(searchTerm = this.searchTerm) {
      let recently_searched = JSON.parse(localStorage.getItem("recently_searched")) || [];

      if (searchTerm.length !== 0 && /^\s*$/.test(searchTerm) === false) {
        if (recently_searched.indexOf(searchTerm) === -1) {
          if (recently_searched.length === 3) {
            recently_searched.shift();
          }
          recently_searched.push(searchTerm)
        } else {
          for (let i = recently_searched.length - 1; i >= 0; i--) {
            if (recently_searched[i] === searchTerm) {
              recently_searched.splice(i, 1);
              recently_searched.push(searchTerm);
            }
          }
        }
      }

      localStorage.setItem("recently_searched", JSON.stringify(recently_searched))
    },
    getRecentlySearched() {
      // Don't try to shorten it to
      // JSON.parse(localStorage.getItem("recently_searched")).reverse()
      // even tho that will work locally.  Something about it breaks the
      // component when building for production without error.
      let recently_searched = JSON.parse(localStorage.getItem("recently_searched")) || [];
      return recently_searched.length ? recently_searched.reverse() : [];
    },
    getSearchTermFromURL() {
      let searchParams = new URLSearchParams(window.location.search);
      let q = searchParams.get('q') || '';

      return q;
    },
    getVariationsMap() {
      return {
        group_by: [
          {
            name: "color",
            field: "data.color",
          },
        ],
        values: {
          image: {
            aggregation: "first",
            field: "data.image_url",
          },
          alter_image: {
            aggregation: "first",
            field: "data.alter_image_url",
          },
        },
        dtype: "object",
      };
    }
  }
}
</script>

<style scoped>
.search-autocomplete-wrapper {
  width: 100%;
  background-color: var(--color-white);
}
@media only screen and (max-width: 767px) {
  .search-autocomplete-wrapper {
    bottom: 10px;
    left: 0;
    position: absolute;
    width: 100vw;
  }
}
@media only screen and (max-width: 767px) {
  .search-autocomplete-wrapper.open {
    bottom: 0;
    left: 0;
    padding-top: 10px;
    position: fixed;
    top: 0;
    width: 100vw;
    z-index: 9999;
  }
}
#search_mini_form {
  width: 100%;
  margin: 0;
}
.label span {
  color: #000;
}
#search_mini_form input#search {
  box-sizing: border-box;
  height: 45px;
  border-radius: 50px;
  width: 100%;
  outline: none;
  padding: 0 5px 0 45px;
  border: 1px solid #4c4c4c;
  color: var(--color-dark-gray);
  font-size: var(--font-size-medium);
  background-color: #fff;
}
#search_mini_form input#search::placeholder {
  font-size: var(--font-size-small);
  color: var(--color-dark-gray);
}
@media only screen and (min-width: 1024px) {
  #search_mini_form input#search {
    padding-right: 45px;
  }
}
#search_mini_form .control {
  margin-left: 10px;
  margin-right: 10px;
  position: relative;
}
@media only screen and (max-width: 767px) {
  .open #search_mini_form .control {
    margin-left: 0;
    margin-right: 54px;
  }

  .open #search_mini_form .control-background-wrapper {
    background: var(--color-white);
    padding: 0 10px 19px;
    position: relative;
    z-index: 1001;
  }
}
#search_mini_form .icon-close,
#search_mini_form .icon-search {
  position: absolute;
  background-size: contain;
  background-position: center;
  top: 50%;
  transform: translateY(-50%);
  background-repeat: no-repeat;
  height: 20px;
  width: 20px;
}
#search_mini_form .icon-close {
  /* undo button */
  appearance: none;
  border: 0;
  background-color: transparent;
  cursor: pointer;

  /* set styles */
  background-image: url("data:image/svg+xml,%3Csvg width='75' height='74' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m2.5 2 70 70m0-70-70 70' stroke='%23000' stroke-width='4' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-size: 18px;
  height: 44px;
  padding: 0.5em;
  right: -54px;
  width: 44px;
  display: none;
}

body.autocomplete-open #search_mini_form .icon-close {
  display: block;
}

#search_mini_form .icon-close:focus-visible {
  outline: var(--brand-primary) auto 1px;
}
#search_mini_form .icon-search {
  background-image: url("data:image/svg+xml,%3Csvg width='21' height='21' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='8' cy='8' r='7.5' stroke='%23000'/%3E%3Cpath d='M13.5 13.5 20 20' stroke='%23000' stroke-linecap='round'/%3E%3C/svg%3E");
  left: 12px;
}
@media only screen and (min-width: 768px) {
  #search_mini_form .icon-close {
    display: none;
    right: 5px;
  }

  .open #search_mini_form .icon-close {
    display: block;
  }
}

#autocomplete-overlay {
  background-color: rgba(0, 0, 0, 0.6);
  display: none;
  position: fixed;
  inset: 0;
  z-index: 1000;
}

#autocomplete-overlay.show {
  display: block;
}
</style>

<!-- This can't be scoped because vue-loader is buggy. -->
<style>
body.autocomplete-open {
  max-height: 100vh;
  overflow: hidden;
}

body.autocomplete-open .page-header {
  background-color: var(--color-white);
  z-index: 1001;
}
</style>
